import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx-js-style';
import { SFOTSalesReportSettings } from 'store/clientIntegrations';
import { KioskSalesByCardType } from 'store/deliveryLog';
import { roundTo2dp } from 'utils/number';

interface ExportKioskSalesReportProps {
  sfotSalesReportSettings: SFOTSalesReportSettings;
  salesReportCurrentYearDate: string;
  salesReportPreviousYearDate: string;
  salesReportDayOfEvent: number;
  oneDayCurrentYearSalesByKiosk: KioskSalesByCardType[] | undefined;
  oneDayPreviousYearSalesByKiosk: KioskSalesByCardType[] | undefined;
  currentYearSalesByKiosk: KioskSalesByCardType[] | undefined;
  previousYearSalesByKiosk: KioskSalesByCardType[] | undefined;
}

/**
 * Merge two KioskSalesByCardType[] results (e.g. from two different client
 * integrations) into one, summing counts/amounts for kiosks that appear in
 * both. Amounts are rounded to 2 decimal places after summing to avoid JS
 * floating point artifacts (e.g. 0.1 + 0.2).
 */
export const mergeKioskSalesByCardType = (
  primary: KioskSalesByCardType[] = [],
  secondary: KioskSalesByCardType[] = []
): KioskSalesByCardType[] => {
  const merged = primary.map((entry) => ({ ...entry }));

  secondary.forEach((entry) => {
    const existing = merged.find((m) => m.kioskName === entry.kioskName);
    if (existing) {
      existing.amexTransactionsCount += entry.amexTransactionsCount;
      existing.amexTransactionsAmount = roundTo2dp(existing.amexTransactionsAmount + entry.amexTransactionsAmount);
      existing.nonAmexTransactionsCount += entry.nonAmexTransactionsCount;
      existing.nonAmexTransactionsAmount = roundTo2dp(
        existing.nonAmexTransactionsAmount + entry.nonAmexTransactionsAmount
      );
    } else {
      merged.push({ ...entry });
    }
  });

  return merged;
};

/**
 * Returns an array of rows to insert into the worksheet.
 *
 * There will be a row for every entry in sfotSalesReportSettings.kioskMap[], with each row
 * representing a single kiosk and it's sales for the current year and previous year.
 */
const convertKioskSalesByCardTypeToRows = (
  sfotSalesReportSettings: SFOTSalesReportSettings,
  currentYearSales: KioskSalesByCardType[] = [],
  previousYearSales: KioskSalesByCardType[] = []
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any[][] => {
  const { kioskMap = [] } = sfotSalesReportSettings;

  return kioskMap.map((kioskMapInst) => {
    // Find the sales data for the corresponding externalId
    const currentYearKioskSales = currentYearSales?.find((ks) => ks.kioskName === kioskMapInst.currentName);
    const previousYearKioskSales = previousYearSales?.find((ks) => ks.kioskName === kioskMapInst.previousName);

    // Add a row for the kiosk, and insert the sales data into the specific columns
    const rowForOneKiosk = Array(29).fill('');
    rowForOneKiosk[0] = kioskMapInst.displayName;
    // Current year
    rowForOneKiosk[2] = currentYearKioskSales?.nonAmexTransactionsCount || 0;
    rowForOneKiosk[3] = currentYearKioskSales?.nonAmexTransactionsAmount || 0;
    rowForOneKiosk[6] = currentYearKioskSales?.amexTransactionsCount || 0;
    rowForOneKiosk[7] = currentYearKioskSales?.amexTransactionsAmount || 0;
    // Previous year
    rowForOneKiosk[14] = previousYearKioskSales?.nonAmexTransactionsCount || 0;
    rowForOneKiosk[15] = previousYearKioskSales?.nonAmexTransactionsAmount || 0;
    rowForOneKiosk[18] = previousYearKioskSales?.amexTransactionsCount || 0;
    rowForOneKiosk[19] = previousYearKioskSales?.amexTransactionsAmount || 0;

    return rowForOneKiosk;
  });
};

/**
 * Build an xlsx file by creating a worksheet, inserting data as rows to the
 * worksheet, and formatting the rows, columns, and individual cells.
 *
 * The layout and design of the worksheet was replicated from what SFT was
 * previously getting from our deprecated TicketMan Java application.
 */
export const exportKioskSalesReport = (exportKioskSalesReportProps: ExportKioskSalesReportProps) => {
  const {
    sfotSalesReportSettings,
    salesReportCurrentYearDate,
    salesReportPreviousYearDate,
    salesReportDayOfEvent,
    oneDayCurrentYearSalesByKiosk,
    oneDayPreviousYearSalesByKiosk,
    currentYearSalesByKiosk,
    previousYearSalesByKiosk
  } = exportKioskSalesReportProps;

  // Create an empty worksheet
  const worksheet = XLSX.utils.json_to_sheet([]);

  // Add the headers for the worksheet & the top section (single day)
  const topSectionHeaders = [
    ['State Fair of Texas'],
    ['Coupon Sales - Kiosk Sales Report'],
    [`Date: ${salesReportCurrentYearDate}`],
    [
      '',
      '',
      '',
      `DAY ${salesReportDayOfEvent} - CURRENT YEAR (${salesReportCurrentYearDate})`,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      `DAY ${salesReportDayOfEvent} - LAST YEAR (${salesReportPreviousYearDate})`,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '% CHANGE',
      '',
      ''
    ],
    [
      '',
      '',
      '',
      'VISA/MC/DISC',
      '',
      '',
      '',
      'AMEX',
      '',
      '',
      '',
      'Total All',
      '',
      '',
      '',
      'VISA/MC/DISC',
      '',
      '',
      '',
      'AMEX',
      '',
      '',
      '',
      'Total All',
      '',
      '',
      '',
      '% Total',
      '',
      '% Avg'
    ],
    [
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      '',
      '',
      ''
    ],
    [
      '',
      'KIOSK#',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      '',
      '',
      ''
    ]
  ];
  XLSX.utils.sheet_add_aoa(worksheet, topSectionHeaders);

  // Add the data for the top section
  const topSectionData = convertKioskSalesByCardTypeToRows(
    sfotSalesReportSettings,
    oneDayCurrentYearSalesByKiosk,
    oneDayPreviousYearSalesByKiosk
  );
  XLSX.utils.sheet_add_aoa(worksheet, topSectionData, { origin: 'B8' });

  // Add empty cells for the totals row of the top section, formulas added later
  const topSectionTotalsRow = topSectionData.length + 9;
  XLSX.utils.sheet_add_aoa(worksheet, [Array(27).fill('')], { origin: `D${topSectionTotalsRow}` });

  // Add the headers for the bottom section (year to date)
  const bottomSectionHeaders = [
    [
      '',
      '',
      '',
      `DAY ${salesReportDayOfEvent} - YEAR TO DATE - CURRENT YEAR (${salesReportCurrentYearDate})`,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      `DAY ${salesReportDayOfEvent} - YEAR TO DATE - LAST YEAR (${salesReportPreviousYearDate})`,
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '% CHANGE',
      '',
      ''
    ],
    [
      '',
      '',
      '',
      'VISA/MC/DISC',
      '',
      '',
      '',
      'AMEX',
      '',
      '',
      '',
      'Total All',
      '',
      '',
      '',
      'VISA/MC/DISC',
      '',
      '',
      '',
      'AMEX',
      '',
      '',
      '',
      'Total All',
      '',
      '',
      '',
      '% Total',
      '',
      '% Avg'
    ],
    [
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      'No. of',
      '',
      '',
      '',
      '',
      '',
      ''
    ],
    [
      '',
      'KIOSK#',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      'Trans',
      'Total $',
      'Avg $',
      '',
      '',
      '',
      ''
    ]
  ];
  const bottomSectionFirstRow = topSectionData.length + 12;
  XLSX.utils.sheet_add_aoa(worksheet, bottomSectionHeaders, { origin: `A${bottomSectionFirstRow}` });

  // Add the data for the bottom section
  const bottomSectionData = convertKioskSalesByCardTypeToRows(
    sfotSalesReportSettings,
    currentYearSalesByKiosk,
    previousYearSalesByKiosk
  );
  XLSX.utils.sheet_add_aoa(worksheet, bottomSectionData, { origin: `B${bottomSectionFirstRow + 4}` });

  // Add empty cells for the totals row of the bottom section, formulas added later
  const bottomSectionTotalsRow = bottomSectionFirstRow + bottomSectionData.length + 5;
  XLSX.utils.sheet_add_aoa(worksheet, [Array(27).fill('')], { origin: `D${bottomSectionTotalsRow}` });

  // Merge cells
  worksheet['!merges'] = [
    // Worksheet headers
    { s: { r: 0, c: 0 }, e: { r: 0, c: 29 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 29 } },
    { s: { r: 2, c: 0 }, e: { r: 2, c: 29 } },
    // Top section headers
    { s: { r: 3, c: 3 }, e: { r: 3, c: 13 } },
    { s: { r: 3, c: 15 }, e: { r: 3, c: 25 } },
    { s: { r: 3, c: 27 }, e: { r: 3, c: 29 } },
    { s: { r: 4, c: 3 }, e: { r: 4, c: 5 } },
    { s: { r: 4, c: 7 }, e: { r: 4, c: 9 } },
    { s: { r: 4, c: 11 }, e: { r: 4, c: 13 } },
    { s: { r: 4, c: 15 }, e: { r: 4, c: 17 } },
    { s: { r: 4, c: 19 }, e: { r: 4, c: 21 } },
    { s: { r: 4, c: 23 }, e: { r: 4, c: 25 } },
    // Bottom section headers
    { s: { r: bottomSectionFirstRow - 1, c: 3 }, e: { r: bottomSectionFirstRow - 1, c: 13 } },
    { s: { r: bottomSectionFirstRow - 1, c: 15 }, e: { r: bottomSectionFirstRow - 1, c: 25 } },
    { s: { r: bottomSectionFirstRow - 1, c: 27 }, e: { r: bottomSectionFirstRow - 1, c: 29 } },
    { s: { r: bottomSectionFirstRow, c: 3 }, e: { r: bottomSectionFirstRow, c: 5 } },
    { s: { r: bottomSectionFirstRow, c: 7 }, e: { r: bottomSectionFirstRow, c: 9 } },
    { s: { r: bottomSectionFirstRow, c: 11 }, e: { r: bottomSectionFirstRow, c: 13 } },
    { s: { r: bottomSectionFirstRow, c: 15 }, e: { r: bottomSectionFirstRow, c: 17 } },
    { s: { r: bottomSectionFirstRow, c: 19 }, e: { r: bottomSectionFirstRow, c: 21 } },
    { s: { r: bottomSectionFirstRow, c: 23 }, e: { r: bottomSectionFirstRow, c: 25 } }
  ];

  // Column meta data (width)
  const columnMetadata = [];
  for (let i = 0; i <= 29; i++) {
    if ([0, 2, 6, 10, 14, 18, 22, 26, 28].includes(i)) {
      columnMetadata.push({ wpx: 12 });
    } else if (i === 1) {
      columnMetadata.push({ wpx: 120 });
    } else if ([4, 8, 12, 16, 20, 24].includes(i)) {
      columnMetadata.push({ wpx: 90 });
    } else if (i === 27) {
      columnMetadata.push({ wpx: 48 });
    } else if (i === 29) {
      columnMetadata.push({ wpx: 53 });
    } else {
      columnMetadata.push({ wpx: 42 });
    }
  }
  worksheet['!cols'] = columnMetadata;

  // Row meta data (width)
  const rowMetadata = [];
  const numberOfRows = topSectionData.length + bottomSectionData.length + 20;
  for (let i = 0; i <= numberOfRows; i++) {
    if (i < 5 || i === bottomSectionFirstRow - 1 || i === bottomSectionFirstRow) {
      rowMetadata.push({ hpx: 16 });
    } else {
      rowMetadata.push({ hpx: 13 });
    }
  }
  worksheet['!rows'] = rowMetadata;

  // Page margins
  worksheet['!margins'] = {
    left: 0.75,
    right: 0.75,
    top: 1,
    bottom: 0.75,
    header: 0.5,
    footer: 0.5
  };

  // Iterate through each cell of the worksheet
  for (const i in worksheet) {
    if (typeof worksheet[i] !== 'object') continue;
    const cell = XLSX.utils.decode_cell(i);

    // Establish a base style
    worksheet[i].s = { font: { name: 'arial', sz: '10' } };

    // Font
    if ([0, 1, 2, 3, 4, bottomSectionFirstRow - 1, bottomSectionFirstRow].includes(cell.r)) {
      worksheet[i].s.font = { name: 'arial', sz: '12', bold: true };
    } else if ([5, 6, bottomSectionFirstRow + 1, bottomSectionFirstRow + 2].includes(cell.r) || cell.c === 1) {
      worksheet[i].s.font = { name: 'arial', sz: '8', bold: true };
    }

    // Text alignment
    if (cell.r < 7 || (cell.r >= bottomSectionFirstRow - 1 && cell.r <= bottomSectionFirstRow + 2)) {
      worksheet[i].s.alignment = { horizontal: 'center' };
    }

    // Fill (background/foreground coloring)
    if (
      (cell.r === 3 && [3, 15, 27].includes(cell.c)) ||
      (cell.r === 4 && [3, 7, 11, 15, 19, 23, 27, 29].includes(cell.c)) ||
      (cell.r === bottomSectionFirstRow - 1 && [3, 15, 27].includes(cell.c)) ||
      (cell.r === bottomSectionFirstRow && [3, 7, 11, 15, 19, 23, 27, 29].includes(cell.c))
    ) {
      worksheet[i].s.fill = { type: 'pattern', patternType: 'solid', fgColor: { rgb: 'c0c0c0' } };
    }

    // Border
    if (cell.r === 3 || cell.r === bottomSectionFirstRow - 1) {
      if ([3, 15, 27].includes(cell.c)) {
        worksheet[i].s.border = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' } };
      } else if ([13, 25, 29].includes(cell.c)) {
        worksheet[i].s.border = { top: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };
      } else if ([4, 5, 6, 7, 8, 9, 10, 11, 12, 16, 17, 18, 19, 20, 21, 22, 23, 24, 28].includes(cell.c)) {
        worksheet[i].s.border = { top: { style: 'thin' }, bottom: { style: 'thin' } };
      }
    }

    // Number format (currency & percentages)
    if ((cell.r > 6 && cell.r < bottomSectionFirstRow) || cell.r > bottomSectionFirstRow + 2) {
      if ([4, 5, 8, 9, 12, 13, 16, 17, 20, 21, 24, 25].includes(cell.c)) {
        worksheet[i].s.numFmt = '#,##0.00;(#,##0.00)';
      } else if ([27, 29].includes(cell.c)) {
        worksheet[i].s.numFmt = '0.00%';
      }
    }

    // Formulas for each row of data
    if (
      (cell.r > 6 && cell.r < topSectionData.length + 7) ||
      (cell.r > bottomSectionFirstRow + 2 && cell.r < bottomSectionTotalsRow - 2)
    ) {
      if (cell.c === 5) {
        // Other average, current year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `IF(D${cell.r + 1},E${cell.r + 1}/D${cell.r + 1},0)` };
      } else if (cell.c === 9) {
        // Amex average, current year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `IF(H${cell.r + 1},I${cell.r + 1}/H${cell.r + 1},0)` };
      } else if (cell.c === 11) {
        // Total transaction count, current year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `D${cell.r + 1}+H${cell.r + 1}` };
      } else if (cell.c === 12) {
        // Total dollar amount, current year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `E${cell.r + 1}+I${cell.r + 1}` };
      } else if (cell.c === 13) {
        // Total average, current year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `IF(L${cell.r + 1},M${cell.r + 1}/L${cell.r + 1},0)` };
      } else if (cell.c === 17) {
        // Other average, previous year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `IF(P${cell.r + 1},Q${cell.r + 1}/P${cell.r + 1},0)` };
      } else if (cell.c === 21) {
        // Amex average, previous year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `IF(T${cell.r + 1},U${cell.r + 1}/T${cell.r + 1},0)` };
      } else if (cell.c === 23) {
        // Total transaction count, previous year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `P${cell.r + 1}+T${cell.r + 1}` };
      } else if (cell.c === 24) {
        // Total dollar amount, previous year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `Q${cell.r + 1}+U${cell.r + 1}` };
      } else if (cell.c === 25) {
        // Total average, previous year
        worksheet[i] = { ...worksheet[i], t: 'n', f: `IF(X${cell.r + 1},Y${cell.r + 1}/X${cell.r + 1},0)` };
      } else if (cell.c === 27) {
        // Change of total dollar amount between years
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(Y${cell.r + 1},((M${cell.r + 1}-Y${cell.r + 1})/Y${cell.r + 1}),0)`
        };
      } else if (cell.c === 29) {
        // Change of average between years
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(Z${cell.r + 1},((N${cell.r + 1}-Z${cell.r + 1})/Z${cell.r + 1}),0)`
        };
      }
    }

    // Formulas for the totals rows of the top and bottom sections
    if (cell.r === topSectionTotalsRow - 1 || cell.r === bottomSectionTotalsRow - 1) {
      const rangeStartRow = cell.r === topSectionTotalsRow - 1 ? 8 : bottomSectionFirstRow + 4;

      if (cell.c === 3) {
        // Other transaction count, current year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(D${rangeStartRow}:D${cell.r})`,
          F: `D${cell.r + 1}:D${cell.r + 1}`
        };
      } else if (cell.c === 4) {
        // Other dollar amount, current year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(E${rangeStartRow}:E${cell.r})`,
          F: `E${cell.r + 1}:E${cell.r + 1}`
        };
      } else if (cell.c === 5) {
        // Other average, current year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(D${cell.r + 1},E${cell.r + 1}/D${cell.r + 1},0)`,
          F: `F${cell.r + 1}:F${cell.r + 1}`
        };
      } else if (cell.c === 7) {
        // Amex transaction count, current year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(H${rangeStartRow}:H${cell.r})`,
          F: `H${cell.r + 1}:H${cell.r + 1}`
        };
      } else if (cell.c === 8) {
        // Amex dollar amount, current year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(I${rangeStartRow}:I${cell.r})`,
          F: `I${cell.r + 1}:I${cell.r + 1}`
        };
      } else if (cell.c === 9) {
        // Amex average, current year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(H${cell.r + 1},I${cell.r + 1}/H${cell.r + 1},0)`,
          F: `J${cell.r + 1}:J${cell.r + 1}`
        };
      } else if (cell.c === 11) {
        // Total transaction count, current year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(L${rangeStartRow}:L${cell.r})`,
          F: `L${cell.r + 1}:L${cell.r + 1}`
        };
      } else if (cell.c === 12) {
        // Total dollar amount, current year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(M${rangeStartRow}:M${cell.r})`,
          F: `M${cell.r + 1}:M${cell.r + 1}`
        };
      } else if (cell.c === 13) {
        // Total average, current year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(L${cell.r + 1},M${cell.r + 1}/L${cell.r + 1},0)`,
          F: `N${cell.r + 1}:N${cell.r + 1}`
        };
      } else if (cell.c === 15) {
        // Other transaction count, previous year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(P${rangeStartRow}:P${cell.r})`,
          F: `P${cell.r + 1}:P${cell.r + 1}`
        };
      } else if (cell.c === 16) {
        // Other dollar amount, previous year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(Q${rangeStartRow}:Q${cell.r})`,
          F: `Q${cell.r + 1}:Q${cell.r + 1}`
        };
      } else if (cell.c === 17) {
        // Other average, previous year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(P${cell.r + 1},Q${cell.r + 1}/P${cell.r + 1},0)`,
          F: `R${cell.r + 1}:R${cell.r + 1}`
        };
      } else if (cell.c === 19) {
        // Amex transaction count, previous year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(T${rangeStartRow}:T${cell.r})`,
          F: `T${cell.r + 1}:T${cell.r + 1}`
        };
      } else if (cell.c === 20) {
        // Amex dollar amount, previous year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(U${rangeStartRow}:U${cell.r})`,
          F: `U${cell.r + 1}:U${cell.r + 1}`
        };
      } else if (cell.c === 21) {
        // Amex average, previous year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(T${cell.r + 1},U${cell.r + 1}/T${cell.r + 1},0)`,
          F: `V${cell.r + 1}:V${cell.r + 1}`
        };
      } else if (cell.c === 23) {
        // Total transaction count, previous year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(X${rangeStartRow}:X${cell.r})`,
          F: `X${cell.r + 1}:X${cell.r + 1}`
        };
      } else if (cell.c === 24) {
        // Total dollar amount, previous year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `SUM(Y${rangeStartRow}:Y${cell.r})`,
          F: `Y${cell.r + 1}:Y${cell.r + 1}`
        };
      } else if (cell.c === 25) {
        // Total average, previous year
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(X${cell.r + 1},Y${cell.r + 1}/X${cell.r + 1},0)`,
          F: `Z${cell.r + 1}:Z${cell.r + 1}`
        };
      } else if (cell.c === 27) {
        // Change of total dollar amount between years
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(Y${cell.r + 1},((M${cell.r + 1}-Y${cell.r + 1})/Y${cell.r + 1}),0)`
        };
      } else if (cell.c === 29) {
        // Change of average between years
        worksheet[i] = {
          ...worksheet[i],
          t: 'n',
          f: `IF(Z${cell.r + 1},((N${cell.r + 1}-Z${cell.r + 1})/Z${cell.r + 1}),0)`
        };
      }
    }
  }

  // Create the workbook and add the worksheet
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Kiosk Sales Report');

  // Buffer to store the generated Excel file
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
  });

  // Download the file
  saveAs(blob, `KioskSalesReport_${format(new Date(), 'yyyyMMddHHmmss')}.xlsx`);
};
